interface Contact {
  id: string;
  name: string;
  email?: string;
  phone?: string;
  avatar?: string;
  source: 'api' | 'file' | 'manual';
  createdAt: string;
  lastInvited?: string;
  inviteCount?: number;
  groups?: string[];
  favorite?: boolean;
}

interface ContactGroup {
  id: string;
  name: string;
  description?: string;
  contactIds: string[];
  createdAt: string;
}

interface ImportResult {
  contacts: Contact[];
  duplicates: Contact[];
  errors: string[];
  totalProcessed: number;
}

interface ContactPermission {
  granted: boolean;
  canRequest: boolean;
  status: 'granted' | 'denied' | 'prompt' | 'not_available';
}

class ContactService {
  private static instance: ContactService;

  static getInstance(): ContactService {
    if (!ContactService.instance) {
      ContactService.instance = new ContactService();
    }
    return ContactService.instance;
  }

  // Check if contacts API is available
  isContactsAvailable(): boolean {
    return 'contacts' in navigator && 'ContactsManager' in window;
  }

  // Get browser compatibility info
  getBrowserCompatibility(): {
    supported: boolean;
    platform: string;
    recommendation: string;
  } {
    const userAgent = navigator.userAgent;
    const isMobile = /Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(userAgent);
    const isChrome = /Chrome/.test(userAgent) && /Google Inc/.test(navigator.vendor);
    const isEdge = /Edg/.test(userAgent);
    
    if (isMobile && (isChrome || isEdge)) {
      return {
        supported: true,
        platform: 'Mobile Chrome/Edge',
        recommendation: 'Contact sync is available on your device'
      };
    } else if (isMobile) {
      return {
        supported: false,
        platform: 'Mobile Browser',
        recommendation: 'Use file upload to import contacts'
      };
    } else {
      return {
        supported: false,
        platform: 'Desktop Browser',
        recommendation: 'Contact sync not available on desktop. Use file upload instead.'
      };
    }
  }

  // Check current permission status
  async checkPermission(): Promise<ContactPermission> {
    if (!this.isContactsAvailable()) {
      return {
        granted: false,
        canRequest: false,
        status: 'not_available'
      };
    }

    try {
      const permissions = await (navigator as any).contacts.query({
        name: 'contacts',
        properties: ['name', 'email', 'tel']
      });

      return {
        granted: true,
        canRequest: false,
        status: 'granted'
      };
    } catch (error: any) {
      if (error.name === 'NotAllowedError') {
        return {
          granted: false,
          canRequest: true,
          status: 'denied'
        };
      } else if (error.name === 'NotFoundError') {
        return {
          granted: false,
          canRequest: true,
          status: 'prompt'
        };
      } else {
        return {
          granted: false,
          canRequest: false,
          status: 'not_available'
        };
      }
    }
  }

  // Request permission to access contacts
  async requestPermission(): Promise<ContactPermission> {
    if (!this.isContactsAvailable()) {
      return {
        granted: false,
        canRequest: false,
        status: 'not_available'
      };
    }

    try {
      const contacts = await (navigator as any).contacts.select(['name', 'email', 'tel'], {
        multiple: true
      });

      return {
        granted: true,
        canRequest: false,
        status: 'granted'
      };
    } catch (error: any) {
      if (error.name === 'NotAllowedError') {
        return {
          granted: false,
          canRequest: true,
          status: 'denied'
        };
      } else if (error.name === 'NotFoundError') {
        return {
          granted: false,
          canRequest: true,
          status: 'prompt'
        };
      } else {
        return {
          granted: false,
          canRequest: false,
          status: 'not_available'
        };
      }
    }
  }

  // Get all contacts
  async getContacts(): Promise<Contact[]> {
    if (!this.isContactsAvailable()) {
      throw new Error('Contacts API not available');
    }

    try {
      const contacts = await (navigator as any).contacts.select(['name', 'email', 'tel'], {
        multiple: true
      });

      return contacts.map((contact: any, index: number) => ({
        id: `contact_${index}_${Date.now()}`,
        name: `${contact.name[0]?.given || ''} ${contact.name[0]?.family || ''}`.trim() || 'Unknown',
        email: contact.email?.[0] || undefined,
        phone: contact.tel?.[0] || undefined,
        avatar: undefined,
        source: 'api' as const,
        createdAt: new Date().toISOString(),
        groups: [],
        favorite: false
      }));
    } catch (error) {
      throw new Error('Failed to access contacts');
    }
  }

  // Search contacts by name
  async searchContacts(query: string): Promise<Contact[]> {
    const contacts = await this.getContacts();
    
    if (!query.trim()) {
      return contacts;
    }

    const searchTerm = query.toLowerCase();
    return contacts.filter(contact => 
      contact.name.toLowerCase().includes(searchTerm) ||
      contact.email?.toLowerCase().includes(searchTerm) ||
      contact.phone?.includes(query)
    );
  }

  // Fallback method for browsers without Contacts API
  async importContactsFromFile(file: File): Promise<Contact[]> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      
      reader.onload = (e) => {
        try {
          const text = e.target?.result as string;
          const contacts = this.parseContactsFile(text, file.name);
          resolve(contacts);
        } catch (error) {
          reject(new Error('Failed to parse contacts file'));
        }
      };

      reader.onerror = () => {
        reject(new Error('Failed to read contacts file'));
      };

      reader.readAsText(file);
    });
  }

  // Parse different contact file formats
  private parseContactsFile(content: string, filename: string): Contact[] {
    const extension = filename.split('.').pop()?.toLowerCase();
    
    switch (extension) {
      case 'csv':
        return this.parseCSV(content);
      case 'vcf':
        return this.parseVCF(content);
      case 'json':
        return this.parseJSON(content);
      default:
        throw new Error(`Unsupported file format: ${extension}`);
    }
  }

  // Parse CSV contacts
  private parseCSV(content: string): Contact[] {
    const lines = content.split('\n');
    const contacts: Contact[] = [];
    
    // Skip header if present
    const startIndex = lines[0].toLowerCase().includes('name') ? 1 : 0;
    
    for (let i = startIndex; i < lines.length; i++) {
      const line = lines[i].trim();
      if (!line) continue;
      
      const values = line.split(',').map(v => v.trim().replace(/"/g, ''));
      
      contacts.push({
        id: `imported_${i}_${Date.now()}`,
        name: values[0] || 'Unknown',
        email: values[1] || undefined,
        phone: values[2] || undefined,
        source: 'file' as const,
        createdAt: new Date().toISOString(),
        groups: [],
        favorite: false
      });
    }
    
    return contacts;
  }

  // Parse VCF contacts
  private parseVCF(content: string): Contact[] {
    const contacts: Contact[] = [];
    const vcardBlocks = content.split('BEGIN:VCARD').filter(block => block.includes('END:VCARD'));
    
    vcardBlocks.forEach((block, index) => {
      const nameMatch = block.match(/FN:(.+)/);
      const emailMatch = block.match(/EMAIL[^:]*:(.+)/);
      const phoneMatch = block.match(/TEL[^:]*:(.+)/);
      
      contacts.push({
        id: `vcf_${index}_${Date.now()}`,
        name: nameMatch?.[1]?.trim() || 'Unknown',
        email: emailMatch?.[1]?.trim() || undefined,
        phone: phoneMatch?.[1]?.trim() || undefined,
        source: 'file' as const,
        createdAt: new Date().toISOString(),
        groups: [],
        favorite: false
      });
    });
    
    return contacts;
  }

  // Parse JSON contacts
  private parseJSON(content: string): Contact[] {
    try {
      const data = JSON.parse(content);
      const contacts = Array.isArray(data) ? data : [data];
      
      return contacts.map((contact: any, index: number) => ({
        id: contact.id || `json_${index}_${Date.now()}`,
        name: contact.name || `${contact.firstName || ''} ${contact.lastName || ''}`.trim() || 'Unknown',
        email: contact.email || contact.emailAddress || undefined,
        phone: contact.phone || contact.phoneNumber || contact.tel || undefined,
        source: 'file' as const,
        createdAt: new Date().toISOString(),
        groups: [],
        favorite: false
      }));
    } catch (error) {
      throw new Error('Invalid JSON format');
    }
  }

  // Enhanced contact management methods

  // Validate contact data
  validateContact(contact: Partial<Contact>): { isValid: boolean; errors: string[] } {
    const errors: string[] = [];
    
    if (!contact.name || contact.name.trim().length < 2) {
      errors.push('Name must be at least 2 characters long');
    }
    
    if (contact.email) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(contact.email)) {
        errors.push('Invalid email format');
      }
    }
    
    if (contact.phone) {
      const phoneRegex = /^[\d\s\-\+\(\)]+$/;
      if (!phoneRegex.test(contact.phone) || contact.phone.replace(/\D/g, '').length < 10) {
        errors.push('Invalid phone number format');
      }
    }
    
    return {
      isValid: errors.length === 0,
      errors
    };
  }

  // Check for duplicate contacts
  findDuplicates(contacts: Contact[], existingContacts: Contact[] = []): Contact[] {
    const duplicates: Contact[] = [];
    
    for (const contact of contacts) {
      const isDuplicate = existingContacts.some(existing => {
        // Check by email
        if (contact.email && existing.email && contact.email.toLowerCase() === existing.email.toLowerCase()) {
          return true;
        }
        // Check by phone
        if (contact.phone && existing.phone && contact.phone.replace(/\D/g, '') === existing.phone.replace(/\D/g, '')) {
          return true;
        }
        // Check by name (less strict)
        if (contact.name.toLowerCase() === existing.name.toLowerCase()) {
          return true;
        }
        return false;
      });
      
      if (isDuplicate) {
        duplicates.push(contact);
      }
    }
    
    return duplicates;
  }

  // Enhanced import with validation and deduplication
  async importContactsFromFileEnhanced(file: File, existingContacts: Contact[] = []): Promise<ImportResult> {
    const result: ImportResult = {
      contacts: [],
      duplicates: [],
      errors: [],
      totalProcessed: 0
    };
    
    try {
      const text = await this.readFileAsText(file);
      const contacts = this.parseContactsFile(text, file.name);
      result.totalProcessed = contacts.length;
      
      for (const contact of contacts) {
        // Validate contact
        const validation = this.validateContact(contact);
        if (!validation.isValid) {
          result.errors.push(`${contact.name}: ${validation.errors.join(', ')}`);
          continue;
        }
        
        // Check for duplicates
        const isDuplicate = this.findDuplicates([contact], existingContacts).length > 0;
        if (isDuplicate) {
          result.duplicates.push(contact);
          continue;
        }
        
        result.contacts.push(contact);
      }
      
      return result;
    } catch (error) {
      result.errors.push(`Failed to process file: ${error instanceof Error ? error.message : 'Unknown error'}`);
      return result;
    }
  }

  // Helper method to read file as text
  private readFileAsText(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => resolve(e.target?.result as string);
      reader.onerror = () => reject(new Error('Failed to read file'));
      reader.readAsText(file);
    });
  }

  // Create contact groups
  createGroup(name: string, description?: string): ContactGroup {
    return {
      id: `group_${Date.now()}`,
      name,
      description,
      contactIds: [],
      createdAt: new Date().toISOString()
    };
  }

  // Add contact to group
  addContactToGroup(contactId: string, groupId: string, contacts: Contact[], groups: ContactGroup[]): void {
    const contact = contacts.find(c => c.id === contactId);
    const group = groups.find(g => g.id === groupId);
    
    if (contact && group) {
      if (!contact.groups) contact.groups = [];
      if (!contact.groups.includes(groupId)) {
        contact.groups.push(groupId);
      }
      if (!group.contactIds.includes(contactId)) {
        group.contactIds.push(contactId);
      }
    }
  }

  // Search contacts with filters
  searchContactsEnhanced(
    query: string, 
    contacts: Contact[], 
    filters: {
      source?: ('api' | 'file' | 'manual')[];
      groups?: string[];
      favorite?: boolean;
      hasEmail?: boolean;
      hasPhone?: boolean;
    } = {}
  ): Contact[] {
    let filteredContacts = contacts;
    
    // Apply text search
    if (query.trim()) {
      const searchTerm = query.toLowerCase();
      filteredContacts = filteredContacts.filter(contact =>
        contact.name.toLowerCase().includes(searchTerm) ||
        contact.email?.toLowerCase().includes(searchTerm) ||
        contact.phone?.includes(query)
      );
    }
    
    // Apply filters
    if (filters.source) {
      filteredContacts = filteredContacts.filter(c => filters.source!.includes(c.source));
    }
    
    if (filters.groups && filters.groups.length > 0) {
      filteredContacts = filteredContacts.filter(c =>
        c.groups && filters.groups!.some(g => c.groups!.includes(g))
      );
    }
    
    if (filters.favorite !== undefined) {
      filteredContacts = filteredContacts.filter(c => c.favorite === filters.favorite);
    }
    
    if (filters.hasEmail) {
      filteredContacts = filteredContacts.filter(c => !!c.email);
    }
    
    if (filters.hasPhone) {
      filteredContacts = filteredContacts.filter(c => !!c.phone);
    }
    
    return filteredContacts;
  }

  // Update contact invitation tracking
  updateInvitationTracking(contactId: string, contacts: Contact[]): void {
    const contact = contacts.find(c => c.id === contactId);
    if (contact) {
      contact.lastInvited = new Date().toISOString();
      contact.inviteCount = (contact.inviteCount || 0) + 1;
    }
  }

  // Get recent contacts
  getRecentContacts(contacts: Contact[], limit: number = 10): Contact[] {
    return contacts
      .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
      .slice(0, limit);
  }

  // Get frequently invited contacts
  getFrequentlyInvitedContacts(contacts: Contact[], limit: number = 10): Contact[] {
    return contacts
      .filter(c => (c.inviteCount || 0) > 0)
      .sort((a, b) => (b.inviteCount || 0) - (a.inviteCount || 0))
      .slice(0, limit);
  }

  // Create a sample contact file for testing
  createSampleContactFile(): string {
    const sampleContacts = [
      'Name,Email,Phone',
      'John Smith,john.smith@email.com,+27 83 123 4567',
      'Sarah Johnson,sarah.j@email.com,+27 82 987 6543',
      'Mike Williams,mike.w@email.com,+27 79 456 7890'
    ];
    
    return sampleContacts.join('\n');
  }

  // Create enhanced sample contact file with more fields
  createEnhancedSampleContactFile(): string {
    const sampleContacts = [
      'Name,Email,Phone,Group,Favorite',
      'John Smith,john.smith@email.com,+27 83 123 4567,Golf Friends,Yes',
      'Sarah Johnson,sarah.j@email.com,+27 82 987 6543,Work Colleagues,No',
      'Mike Williams,mike.w@email.com,+27 79 456 7890,Golf Friends,Yes',
      'Emma Brown,emma.brown@email.com,+27 76 234 5678,Family,No',
      'David Jones,david.jones@email.com,+27 83 345 6789,Golf Friends,No'
    ];
    
    return sampleContacts.join('\n');
  }
}

export default ContactService.getInstance();
